import axios, { AxiosStatic } from 'axios';
import { AuthentificationService } from '.';

const axiosInstance = axios.create({
    baseURL: process.env.VUE_APP_WEB_API_URL,
});

axiosInstance.interceptors.request.use(
    (config) => {
        // Do something before request is sent
        return config;
    },
    (error) => {
        // Do something with request error
        return Promise.reject(error);
    },
);

// Add a response interceptor
axiosInstance.interceptors.response.use(
    (response) => {
        // Do something with response data
        return response;
    },
    (error) => {
        // Do something with response error
        return Promise.reject(error);
    },
);

class HttpRequest {

    /**
     * The static method that controls the access to the singleton instance.
     *
     * This implementation let you subclass the Singleton class while keeping
     * just one instance of each subclass around.
     */
    public static getInstance(): HttpRequest {
        if (!HttpRequest.instance) {
            HttpRequest.instance = new HttpRequest();
        }

        return HttpRequest.instance;
    }
    /**
     * Credits: https://refactoring.guru/design-patterns/singleton/typescript/example
     */
    private static instance: HttpRequest;
    public axios: AxiosStatic;

    private constructor() {
        this.axios = axios;
    }

    public async setHeader(header?: any) {
        if (header != null) {
            for (const key in header) {
                if (header.hasOwnProperty(key)) {
                    const element = header[key];
                    axiosInstance.defaults.headers.common[key] = header[key];
                }
            }
        }
        axiosInstance.defaults.headers.post['Content-Type'] = 'application/json';
    }

    public async get(methodName: string, data: any) {
        const auth = await this.getAuthHeader(true);
        this.setHeader(auth);
        return axiosInstance.get(methodName, {
            params: data,
        });
    }

    public async post(methodName: string, data: any) {
        const auth = await this.getAuthHeader(true);
        this.setHeader(auth);
        return axiosInstance.post(methodName, data);
    }

    public put(methodName: string, data: any) {
        return axiosInstance.put(methodName, data);
    }

    public async delete(methodName: string, bodyData?: any, queryData?: any) {
        const auth = await this.getAuthHeader(true);
        this.setHeader(auth);
        axiosInstance.defaults.headers.common['Content-Type'] = 'application/json';
        return axiosInstance.delete(methodName, {
            data: bodyData,
            params: queryData,
        });
    }

    public request(type: 'GET' | 'POST' | 'PUT' | 'DELETE', url: string, params: any, body: any, responseType: 'json' | 'arraybuffer' | 'blob' | 'document' | 'text' | 'stream' = 'json') {
        switch (type) {
            case 'GET':
                return axios.get(url, { params, responseType });
            case 'POST':
                return axios.post(url, body, { params, responseType });
            case 'PUT':
                return axios.put(url, body, { params, responseType });
            case 'DELETE':
                return axios.delete(url, { params, responseType });
            default:
                return axios.get(url, { params, responseType });
        }
    }

    private async getAuthHeader(userToken: boolean = false) {
        const auth: any = { Authorization: '' };
        await AuthentificationService.GetUser().then((user: Oidc.User | null) => {
            if (user != null) {
                auth.Authorization = `bearer ${user.access_token}`;
                if (userToken === true) {
                    auth.User = user.id_token;
                }
            }
        });
        return auth;
    }
}

export default HttpRequest;
